import React from 'react';
import { makeStyles } from 'tss-react/mui';

import { FILES_PAGE } from 'src/constants';
import { useAppSelector } from 'src/hooks/useStore';
import { FilesBreadcrumbs } from 'src/legacy/components/Files/FilesBreadcrumbs';
import { selectClientsMap } from 'src/store/clients/types';
import { selectFileChannelsMap } from 'src/store/files/selectors';
import { GraySmall } from 'src/theme/colors';
import { regular } from 'src/theme/weights';

import { NotificationTableRowData } from './InboxContent';

interface NotificationTableBreadcrumbRendererProps {
  data: NotificationTableRowData;
}

const useStyles = makeStyles()(() => ({
  breadcrumbs: {
    background: 'transparent',
    flexWrap: 'nowrap',
    padding: 0,
  },
  lastChip: {
    '& .MuiChip-label, & .MuiChip-icon  ': {
      fontWeight: regular,
      color: GraySmall,
    },
  },
}));

export const NotificationTableBreadcrumbRenderer = (
  props: NotificationTableBreadcrumbRendererProps,
) => {
  const { classes } = useStyles();
  const {
    data: { location, channelId },
  } = props;
  const baseFilesUrl = `${FILES_PAGE.path}?channelId=${channelId || ''}`;

  const fileChannelsMap = useAppSelector(selectFileChannelsMap);
  const activeClientsMap = useAppSelector(selectClientsMap);

  /**
   * get the label of first breadcrumb
   */
  const getFirstNodeLabel = React.useMemo(() => {
    if (!channelId) {
      return '';
    }

    const selectedChannel = fileChannelsMap.get(channelId);
    if (selectedChannel) {
      // if individual client's channel the label will be client name
      if (selectedChannel.fields?.singleClientId) {
        const { memberIDs = {} } = selectedChannel.additionalFields || {};
        const channelMemberId = Object.keys(memberIDs).find((memberId) =>
          activeClientsMap.has(memberId),
        );

        if (channelMemberId) {
          const client = activeClientsMap.get(channelMemberId);
          if (client) {
            return `${client.fields?.givenName}'s files`;
          }
        }
      }
      // for channels having more than 1 client we will use "Group's files" as label
      return `Group's files`;
    }

    return '';
  }, [fileChannelsMap, activeClientsMap, channelId]);

  const filePathParts = location
    .split('/')
    .filter((part: string) => part !== '');

  if (!channelId) return '';

  return (
    <div data-testid="notification-file-breadcrumb">
      <FilesBreadcrumbs
        firstNodeLabel={getFirstNodeLabel}
        selectedChannelId={channelId}
        filePathParts={filePathParts}
        baseURL={baseFilesUrl}
        selectedFilePath={location}
        isSmallWidth={false}
        hasToolbar={false}
        className={classes.breadcrumbs}
        showRootChip
        lastChipClassname={classes.lastChip}
        nestedMenuSize={filePathParts.length - 1}
      />
    </div>
  );
};
